import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      // Purple for “Start Argument,” “Add to Argument” etc.
      main: '#8E44AD',
      contrastText: '#FFFFFF',
    },
    secondary: {
      // Yellow for “Finalize Argument”
      main: '#F1C40F',
      contrastText: '#000000',
    },
    background: {
      default: '#FFFFFF', // in case the gradient doesn't fill the page
      paper: '#FFFFFF',   // White cards/paper
    },
    text: {
      primary: '#2F2145', // or any dark color for main text
      secondary: '#F39C12', // optional accent
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: {
      fontSize: '1.7rem',
      fontWeight: 600,
      color: '#4a346d',
      fontFamily: "Poppins"
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#2F2145',
      fontFamily: "Nunito"
    },
    h3: {
      fontWeight: 'bold',
      color: '#4a346d',
      fontSize: '1.3rem',
      fontFamily: "Nunito"
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      color: '#2F2145',
      fontFamily: "Nunito"
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      color: '#2F2145',
      fontFamily: "Nunito"
    },
    body2: {
      fontSize: '1rem',
      fontWeight: 400,
      color: '#FAFAFA',
      fontFamily: "Nunito"
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          height: '100%',
          margin: 0,
          padding: 0,
        },
        body: {
          minHeight: '100vh',
          /* 
            2-layer gradient:
            - The top layer: transitions from transparent to white at ~33%
            - The bottom layer: left-to-right purple→yellow→orange
          */
          background: `
            linear-gradient(
              to bottom,
              rgba(255,255,255,0) 0%,
              rgba(255,255,255,1) 40%,
              rgba(255,255,255,1) 100%
            ),
            linear-gradient(
              to right,
              #8E44AD 0%,
              #F1C40F 60%,
              #F39C12 100%
            )
          `,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
          color: '#2F2145',
          fontFamily: 'Roboto, Arial, sans-serif',
        },
        // Basic link styles
        a: {
          color: '#0645AD', // a typical "blue" link color
          textDecoration: 'none',
          transition: 'color 0.3s ease, background-color 0.3s ease',
        },
        'a:hover': {
          textDecoration: 'underline',
        },
        // Basic block elements
        ol: {
          paddingLeft: '20px',
        },
        // Global <input>/<textarea> fallback styling
        input: {
          borderRadius: '5px',
          border: '1px solid #8E44AD',
          padding: '10px',
          fontFamily: 'inherit',
          fontSize: '16px',
        },
        'input:focus': {
          outline: 'none',
          borderColor: '#F1C40F',
        },
        textarea: {
          borderRadius: '5px',
          border: '1px solid #8E44AD',
          padding: '10px',
          fontFamily: 'inherit',
          fontSize: '16px',
        },
        'textarea:focus': {
          outline: 'none',
          borderColor: '#F1C40F',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        // Shared style for all buttons
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          padding: '12px 24px', 
          fontWeight: 700,    // or 700 if you prefer
          fontSize: '1rem',   // ensure a consistent size across all buttons
          fontFamily: 'Nunito',
        },
        // Purple button
        containedPrimary: {
          backgroundColor: '#8E44AD',
          color: '#FAFAFA',
          '&:hover': {
            backgroundColor: '#9B59B6',
          },
        },
        // Yellow button
        containedSecondary: {
          backgroundColor: '#F1C40F',
          color: '#2F2145', // purple text on yellow
          '&:hover': {
            backgroundColor: '#f4d146',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FAFAFA', // a light background for contrast
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#8E44AD',
            },
            '&:hover fieldset': {
              borderColor: '#9B59B6',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#8E44AD',
            },
          },
          // Dark text + label
          '& .MuiFormLabel-root': {
            color: '#2F2145',
          },
          '& .MuiOutlinedInput-input': {
            color: '#2F2145',
          },
          // Adjust label positioning if needed
          '& .MuiFormLabel-root.MuiInputLabel-outlined': {
            transform: 'translate(14px, 16px) scale(1)',
          },
          '& .MuiFormLabel-root.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -2px) scale(0.75)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: theme.spacing(2), // 8px
          paddingBottom: theme.spacing(2),
          textAlign: 'center',
          alignItems: 'center',
          border: '1px solid',
          borderColor: theme.palette.divider,
          marginBottom: theme.spacing(2), // 16px
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper, // Ensures consistency with theme
        }),
      },
    },

  },
});

export default theme;
